const ResearchLibrary = () => import("@/pages/Research/ResearchLibrary.vue");
const ResearchResults = () => import("@/pages/Research/ResearchResults.vue");
const ResearchStudio = () =>
  import("@/pages/ResearchStudio/ResearchStudio.vue");
const QuestionBank = () => import("@/pages/ResearchStudio/QuestionBank.vue");
const SurveyDetails = () => import("@/pages/ResearchStudio/SurveyDetails.vue");
const AudienceExplorer = () =>
  import("@/pages/AudienceExplorer/AudienceExplorer.vue");
const ConsumerInsights = () =>
  import("@/pages/AudienceExplorer/ConsumerInsights.vue");
const Demographics = () => import("@/pages/AudienceExplorer/Demographics.vue");
const PolicyInsights = () =>
  import("@/pages/AudienceExplorer/PolicyInsights.vue");
const BehavioralInsights = () =>
  import("@/pages/AudienceExplorer/BehavioralInsights.vue");
const InfluenceInsights = () =>
  import("@/pages/AudienceExplorer/InfluenceInsights.vue");
const MediaMix = () => import("@/pages/AudienceExplorer/MediaMix.vue");
const TvViewership = () => import("@/pages/AudienceExplorer/TvViewership.vue");
const AudienceBuilder = () => import("@/pages/AudienceBuilder.vue");
const Account = () => import("@/pages/Account.vue");
const CableRanker = () => import("@/pages/CableRanker.vue");
const CampaignEffectiveness = () =>
  import("@/pages/CampaignEffectiveness/CampaignEffectiveness.vue");
const ConfigureCampaign = () =>
  import("@/pages/CampaignEffectiveness/ConfigureCampaign.vue");
const CampaignDetails = () =>
  import("@/pages/CampaignEffectiveness/CampaignDetails.vue");
const ForgotPassword = () => import("@/pages/ForgotPassword.vue");
const Login = () => import("@/pages/Login.vue");
const Maintenance = () => import("@/pages/Maintenance.vue");
const ManageAudiences = () => import("@/pages/ManageAudiences.vue");
const DownloadCenter = () => import("@/pages/ControlCenter/DownloadCenter.vue");
const AudienceCatalog = () =>
  import("@/pages/ControlCenter/AudienceCatalog.vue");
const KnowledgeCenter = () =>
  import("@/pages/ControlCenter/KnowledgeCenter.vue");
const Permissioning = () => import("@/pages/ControlCenter/Permissioning.vue");
const PlatformAdmin = () => import("@/pages/ControlCenter/PlatformAdmin.vue");
const SystemControls = () => import("@/pages/ControlCenter/SystemControls.vue");
const PoliticalPaidMedia = () => import("@/pages/PoliticalPaidMedia.vue");
const ReachAndFrequency = () => import("@/pages/ReachAndFrequency.vue");
const AdExplorer = () => import("@/pages/AdExplorer.vue");
const ResetPassword = () => import("@/pages/ResetPassword.vue");
const Settings = () => import("@/pages/Settings.vue");
const FileSharing = () => import("@/pages/FileSharing.vue");
const Theme = () => import("@/pages/Theme.vue");
const TVRatings = () => import("@/pages/TVRatings.vue");
import { useUserStore } from "@/stores/UserStore";
import { useThemeStore } from "@/stores/ThemeStore";
import { initAudienceExplorer } from "@/helpers/audienceExplorer";
import { logSurveyDetailsPageVisit } from "@/helpers/researchStudio";

const routes = [
  {
    path: "/",
    children: [
      {
        path: "/:pathMatch(.*)*",
        redirect: "/",
      },
      {
        path: "/:meta_audience_id?/:geography_id?",
        name: "home",
        redirect: { name: "audience_explorer" },
      },
      {
        path: "maintenance",
        name: "maintenance",
        component: Maintenance,
      },
      {
        path: "login",
        name: "login",
        component: Login,
      },
      {
        path: "forgot-password",
        name: "forgot_password",
        component: ForgotPassword,
      },
      {
        path: "reset-password",
        name: "reset_password",
        component: ResetPassword,
      },
      {
        path: "logout",
        name: "logout",
        beforeEnter() {
          const userStore = useUserStore();
          return userStore.logout().then(() => "/login");
        },
      },
      {
        path: "theme",
        name: "theme",
        component: Theme,
        meta: {
          auth: true,
          permissions: ["internal_user"],
        },
      },
      {
        path: "account",
        name: "account",
        component: Account,
        meta: { auth: true, title: "Account" },
      },
      {
        path: "file-sharing",
        name: "file_sharing",
        component: FileSharing,
        meta: {
          auth: true,
          title: "File Sharing",
        },
      },
      {
        path: "settings",
        name: "settings",
        component: Settings,
        meta: {
          auth: true,
          permissions: [
            "manage_orgs",
            "manage_users",
            "manage_org_users",
            "manage_external_users",
            "manage_roles",
            "manage_tableau_workbooks",
            "manage_clients",
            "manage_pricing_tiers",
            "feature_flags",
          ],
          title: "Settings",
        },
      },
      {
        path: "research",
        meta: {
          auth: true,
          breadcrumb: "Research",
          menuIcon: "mdi-book-open-variant-outline",
          disabled: () => {
            const themeStore = useThemeStore();
            return themeStore.activeTheme?.name !== "tunnl";
          },
        },
        children: [
          {
            path: "research-library",
            name: "research_library",
            component: ResearchLibrary,
            meta: {
              auth: true,
              beta: true,
              permissions: ["internal_user"],
              menuLabel: "Research Library",
              breadcrumb: {
                title: "Research Library",
                to: { name: "research_library" },
              },
              title: "Research Library",
            },
          },
          {
            path: "research-results",
            name: "research_results",
            component: ResearchResults,
            meta: {
              auth: true,
              beta: true,
              permissions: ["internal_user"],
              menuLabel: "Research Results",
              breadcrumb: {
                title: "Research Results",
                to: { name: "research_results" },
              },
              title: "Research Results",
            },
          },
          {
            path: "research-studio",
            meta: {
              auth: true,
              menuLabel: "Research Studio",
              breadcrumb: {
                title: "Research Studio",
                to: { name: "research_studio" },
              },
              title: "Research Studio",
            },
            children: [
              {
                path: "",
                name: "research_studio",
                component: ResearchStudio,
              },
              {
                path: "survey-details/:survey_id",
                name: "survey_details",
                component: SurveyDetails,
                meta: {
                  breadcrumb: "survey_name",
                  customLogging: true,
                  title: "Research Studio",
                },
                beforeEnter(to, from) {
                  logSurveyDetailsPageVisit(to, from);
                },
              },
            ],
          },
          {
            path: "question-bank",
            name: "question_bank",
            component: QuestionBank,
            meta: {
              auth: true,
              breadcrumb: "Question Bank",
              title: "Question Bank",
            },
          },
        ],
      },
      {
        path: "audiences",
        meta: {
          auth: true,
          breadcrumb: "Audiences",
          menuIcon: "mdi-account-outline",
        },
        children: [
          {
            path: "audience-explorer/:meta_audience_id?/:geography_id?",
            meta: {
              auth: true,
              menuLabel: "Audience Explorer",
              breadcrumb: {
                title: "Audience Explorer",
                to: { name: "audience_explorer" },
              },
              customLogging: true,
              title: "Audience Explorer",
            },
            beforeEnter(to, from) {
              return initAudienceExplorer(to, from);
            },
            children: [
              {
                path: "",
                name: "audience_explorer",
                component: AudienceExplorer,
              },
              {
                path: "consumer-insights",
                name: "audience_explorer_consumer_insights",
                component: ConsumerInsights,
                meta: {
                  breadcrumb: "Consumer Insights",
                  title: "Consumer Insights",
                },
              },
              {
                path: "demographics",
                name: "audience_explorer_demographics",
                component: Demographics,
                meta: { breadcrumb: "Demographics", title: "Demographics" },
              },
              {
                path: "policy-insights",
                name: "audience_explorer_policy_insights",
                component: PolicyInsights,
                meta: {
                  breadcrumb: "Policy Insights",
                  title: "Policy Insights",
                },
              },
              {
                path: "behavioral-insights",
                name: "audience_explorer_behavioral_insights",
                component: BehavioralInsights,
                meta: {
                  breadcrumb: "Behavioral Insights",
                  title: "Behavioral Insights",
                },
              },
              {
                path: "influence-insights",
                name: "audience_explorer_influence_insights",
                component: InfluenceInsights,
                meta: {
                  breadcrumb: "Influence Insights",
                  title: "Influence Insights",
                },
              },
              {
                path: "tv-viewership",
                name: "audience_explorer_tv_viewership",
                component: TvViewership,
                meta: {
                  breadcrumb: "TV Viewership",
                  title: "TV Viewership",
                },
              },
              {
                path: "media-mix",
                name: "audience_explorer_media_mix",
                component: MediaMix,
                meta: {
                  breadcrumb: "Media Mix",
                  title: "Media Mix",
                },
              },
            ],
          },
          {
            path: "audience-builder",
            name: "audience_builder",
            component: AudienceBuilder,
            meta: {
              auth: true,
              permissions: ["audience_builder"],
              breadcrumb: "Audience Builder",
              title: "Audience Builder",
            },
          },
          {
            path: "manage-audiences",
            name: "manage_audiences",
            component: ManageAudiences,
            meta: {
              auth: true,
              permissions: ["audience_builder"],
              breadcrumb: "Manage Audiences",
              title: "Manage Audiences",
            },
          },
        ],
      },
      {
        path: "planning",
        meta: {
          auth: true,
          breadcrumb: "Planning",
          menuIcon: "mdi-order-bool-ascending-variant",
        },
        children: [
          {
            path: "tv-ratings",
            name: "tv_ratings",
            component: TVRatings,
            meta: {
              auth: true,
              permissions: ["tv_ratings"],
              breadcrumb: "TV Ratings",
              title: "TV Ratings",
            },
          },
          {
            path: "vamos-tv-ratings",
            name: "vamos_tv_ratings",
            component: TVRatings,
            meta: {
              auth: true,
              univision: true,
              breadcrumb: "VAMOS TV Ratings",
              color: "primary",
              title: "VAMOS TV Ratings",
            },
          },
          {
            path: "cable-ranker",
            name: "cable_ranker",
            component: CableRanker,
            meta: {
              auth: true,
              permissions: ["cable_ranker"],
              title: "Cable Ranker",
            },
          },
        ],
      },
      {
        path: "measurement",
        meta: {
          auth: true,
          breadcrumb: "Measurement",
          menuIcon: "mdi-chart-line",
        },
        children: [
          {
            path: "reach-and-frequency",
            name: "reach_&_frequency",
            component: ReachAndFrequency,
            meta: {
              auth: true,
              breadcrumb: "Reach & Frequency",
              permissions: ["reach_&_frequency"],
              title: "Reach & Frequency",
            },
          },
          {
            path: "ad-explorer",
            name: "ad_explorer",
            component: AdExplorer,
            meta: {
              auth: true,
              breadcrumb: "Ad Explorer",
              permissions: ["reach_&_frequency"],
              requireAllPermissions: true,
              title: "Ad Explorer",
            },
          },
          {
            path: "political-paid-media",
            name: "political_paid_media",
            component: PoliticalPaidMedia,
            meta: {
              auth: true,
              permissions: ["political_paid_media"],
              title: "Political Paid Media",
            },
          },
          {
            path: "campaign-effectiveness",
            meta: {
              auth: true,
              menuLabel: "Campaign Effectiveness",
              beta: true,
              breadcrumb: {
                title: "Campaign Effectiveness",
                to: { name: "campaign_effectiveness" },
              },
              title: "Campaign Effectiveness",
            },
            children: [
              {
                path: "",
                name: "campaign_effectiveness",
                component: CampaignEffectiveness,
              },
              {
                path: "configure/:campaign_id",
                name: "campaign_effectiveness_configure",
                component: ConfigureCampaign,
                meta: {
                  breadcrumb: "Configure",
                  title: "Campaign Effectiveness",
                },
              },
              {
                path: "details/:campaign_id",
                name: "campaign_effectiveness_details",
                component: CampaignDetails,
                meta: {
                  breadcrumb: "Details",
                  title: "Campaign Effectiveness",
                },
              },
            ],
          },
        ],
      },
      {
        path: "control-center",
        meta: {
          auth: true,
          breadcrumb: "Control Center",
          menuIcon: "mdi-view-grid-outline",
        },
        children: [
          {
            path: "download-center",
            name: "download_center",
            component: DownloadCenter,
            meta: {
              auth: true,
              menuLabel: "Download Center",
              breadcrumb: "Download Center",
              title: "Download Center",
            },
          },
          {
            path: "knowledge-center",
            name: "knowledge_center",
            component: KnowledgeCenter,
            meta: {
              auth: true,
              menuLabel: "Knowledge Center",
              breadcrumb: "Knowledge Center",
              title: "Knowledge Center",
            },
          },
          {
            path: "audience-catalog",
            name: "audience_catalog",
            component: AudienceCatalog,
            meta: {
              auth: true,
              menuLabel: "Audience Catalog",
              breadcrumb: "Audience Catalog",
              title: "Audience Catalog",
            },
          },
          {
            path: "permissioning",
            name: "permissioning",
            component: Permissioning,
            meta: {
              auth: true,
              permissions: ["manage_orgs"],
              menuLabel: "Permissioning",
              breadcrumb: "Permissioning",
              title: "Permissioning",
              killSwitch: false,
            },
          },
          {
            path: "system-controls",
            name: "system_controls",
            component: SystemControls,
            meta: {
              auth: true,
              permissions: ["manage_users", "feature_flags", "manage_roles"],
              menuLabel: "System Controls",
              breadcrumb: "System Controls",
              title: "System Controls",
              killSwitch: false,
            },
          },
          {
            path: "platform-admin",
            name: "platform_admin",
            component: PlatformAdmin,
            meta: {
              auth: true,
              permissions: ["internal_user"],
              menuLabel: "Platform Admin",
              breadcrumb: "Platform Admin",
              title: "Platform Admin",
              killSwitch: false,
            },
          },
        ],
      },
    ],
  },
];

export default routes;
